import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { API_CONSTANTS } from "../../../config/api-constants";
import { DEFAULT_SETTINGS } from "../../pages/ConfigurationPage";
import { Button } from "../ui/button";
import { ChevronLeft, ChevronRight, Clock, User, Loader2 } from "lucide-react";
import {
  format,
  addMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  isBefore,
  startOfDay,
  getDay,
  addDays,
} from "date-fns";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Label } from "../ui/label";
import { cn } from "../../lib/utils";

const getConfig = () => {
  try {
    const saved = localStorage.getItem("booking_config");
    return saved ? JSON.parse(saved) : {};
  } catch {
    return {};
  }
};

// Converts "H:MM AM/PM" or "HH:MM:SS" (24h) to total minutes from midnight
const parseTimeToMinutes = (timeStr) => {
  if (!timeStr) return null;
  const str = timeStr.trim();
  // Handle full datetime "2026-05-18 08:00:00" → extract time part
  const parts = str.split(" ");
  const timePart = parts.length >= 2 ? parts[1] : parts[0];
  if (timePart.includes("AM") || timePart.includes("PM") || str.includes("AM") || str.includes("PM")) {
    const [time, period] = str.split(" ");
    const [h, m] = time.split(":").map(Number);
    let hours = h;
    if (period === "PM" && h !== 12) hours += 12;
    if (period === "AM" && h === 12) hours = 0;
    return hours * 60 + (m || 0);
  }
  const [h, m] = timePart.split(":").map(Number);
  return h * 60 + (m || 0);
};

// Converts a UTC datetime string "2026-05-18 08:00:00" to local "HH:MM".
// Falls back to plain time extraction for strings that are already local.
const utcToLocalHHMM = (dateTimeStr) => {
  if (!dateTimeStr) return "";
  const str = dateTimeStr.trim();
  // If it contains a date part, treat as UTC and convert to local
  if (str.includes(" ") && str.match(/^\d{4}-\d{2}-\d{2}/)) {
    const d = new Date(str.replace(" ", "T") + "Z"); // append Z → parse as UTC
    const h = String(d.getHours()).padStart(2, "0");
    const m = String(d.getMinutes()).padStart(2, "0");
    return `${h}:${m}`;
  }
  // Plain "HH:MM:SS" or "HH:MM" — extract as-is
  return str.substring(0, 5);
};

// Formats "HH:MM" to "H:MM AM/PM"
const format24hTo12h = (timeStr) => {
  if (!timeStr) return "";
  const clean = utcToLocalHHMM(timeStr); // handle full datetimes too
  const [h, m] = clean.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, "0")} ${period}`;
};

// Given a window start "HH:MM", end "HH:MM", and duration in minutes,
// generate all possible slot start times that fit within the window
const generateSlotsFromWindow = (windowStart, windowEnd, durationMinutes) => {
  const startMins = parseTimeToMinutes(windowStart);
  const endMins   = parseTimeToMinutes(windowEnd);
  if (startMins === null || endMins === null || durationMinutes <= 0) return [];
  const slots = [];
  let current = startMins;
  while (current + durationMinutes <= endMins) {
    const slotEndMins = current + durationMinutes;
    const toHHMM = (mins) => `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
    slots.push({ start: toHHMM(current), end: toHHMM(slotEndMins) });
    current += durationMinutes; // non-overlapping slots
  }
  return slots;
};

const isPastCutoff = (cutoffTime, startTime) => {
  const cutoff = cutoffTime || getConfig().nextDayCutoffTime || "18:00";
  const start  = startTime  || "08:00";
  const [ch, cm] = cutoff.split(":").map(Number);
  const [sh, sm] = start.split(":").map(Number);
  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const cutoffMinutes  = ch * 60 + cm;
  const startMinutes   = sh * 60 + sm;
  return currentMinutes >= cutoffMinutes || currentMinutes < startMinutes;
};

const DateTimeForm = ({ formData, handleFormChange, validationErrors, schedulerSettings = null }) => {
  const today = startOfDay(new Date());
  const [currentMonth, setCurrentMonth] = useState(startOfMonth(today));
  const [inspectorSchedule, setInspectorSchedule] = useState([]);
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(false);
  const prevDateRef = useRef(null);
  const prevDurationRef = useRef(null);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDayOfWeek = getDay(monthStart);
  const dayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const totalServiceDuration = Object.values(formData.serviceDurations || {}).reduce(
    (sum, d) => sum + d,
    0
  );

  // On mount: restore cached inspector schedule and seed the prev refs so the
  // fetch effect can tell the difference between a remount and a real date/duration change.
  useEffect(() => {
    const hasCachedSchedule = formData.availableInspectors?.length > 0;
    if (hasCachedSchedule) {
      setInspectorSchedule(formData.availableInspectors);
      // Seed refs so the fetch effect knows nothing changed
      prevDateRef.current = formData.appointmentDate
      ? format(formData.appointmentDate, "yyyy-MM-dd")
      : null;
      prevDurationRef.current = totalServiceDuration;
    }
    // If there's no cached schedule (e.g. services changed while unmounted),
    // leave prevDateRef null so the fetch effect treats it as a new request.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch inspector schedule only when the date or service duration actually changes.
  useEffect(() => {
    if (!formData.appointmentDate) return;

    const dateStr = format(formData.appointmentDate, "yyyy-MM-dd");
    const dateChanged = dateStr !== prevDateRef.current;
    const durationChanged = totalServiceDuration !== prevDurationRef.current;

    // Nothing changed — component just remounted, skip fetch and reset.
    if (!dateChanged && !durationChanged) return;

    prevDateRef.current = dateStr;
    prevDurationRef.current = totalServiceDuration;

    const companyKey = sessionStorage.getItem("company_key") || "";
    const pad = (n) => String(n).padStart(2, "0");

    const localDateWithTime = (localDate, timeStr) => {
      const [h = 0, m = 0] = (timeStr || "00:00").split(":").map(Number);
      const dt = new Date(localDate.getFullYear(), localDate.getMonth(), localDate.getDate(), h, m, 0);
      return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())} ${pad(dt.getUTCHours())}:${pad(dt.getUTCMinutes())}:${pad(dt.getUTCSeconds())}`;
    };

    const selectedLocalDate = formData.appointmentDate;
    const isSelectedToday = isSameDay(selectedLocalDate, today);
    const nextDayCutoffEnabled = schedulerSettings?.nextday_cutoff === 1;

    // For same-day, use the browser's current time as start_time (converted to UTC via localDateWithTime).
    // For all other dates, use the scheduler's configured start_time (or default) as-is, without buffer.
    const startTimeStr = (() => {
      if (isSelectedToday) {
        const now = new Date();
        const currentMins = now.getHours() * 60 + now.getMinutes();
        const bufferedMins = currentMins + 30;
        // Clamp to 23:59 so a late request never overflows the hour (e.g. "24:30").
        const roundedMins = Math.min(Math.ceil(bufferedMins / 30) * 30, 24 * 60 - 1);
        return `${pad(Math.floor(roundedMins / 60))}:${pad(roundedMins % 60)}`;
      }
      return schedulerSettings?.start_time || DEFAULT_SETTINGS.start_time;
    })();

    const endTimeStr = isSelectedToday && nextDayCutoffEnabled && schedulerSettings?.cutoff_time
      ? schedulerSettings.cutoff_time
      : (schedulerSettings?.end_time || DEFAULT_SETTINGS.end_time);

    const payload = {
      company_key: companyKey,
      duration: totalServiceDuration || 60,
      date: dateStr,
      start_time:  localDateWithTime(selectedLocalDate, startTimeStr),
      end_time:    localDateWithTime(selectedLocalDate, endTimeStr),
    };

    setIsLoadingSchedule(true);
    setInspectorSchedule([]);
    handleFormChange("availableInspectors", []);
    handleFormChange("preferredInspector", "any");
    handleFormChange("appointmentTime", "");

    axios
      .post(API_CONSTANTS.GET_INSPECTORS_SCHEDULE, payload)
      .then((res) => {
        const raw = res.data?.data || [];
        const duration = totalServiceDuration || 60;
        const normalised = (Array.isArray(raw) ? raw : []).map((item) => {
          const windows = item.available_slots || item.slots || [];
          const slots = windows.flatMap((w) => {
            const windowStart = utcToLocalHHMM(w.start_time ?? w.start ?? "");
            const windowEnd   = utcToLocalHHMM(w.end_time   ?? w.end   ?? "");
            return generateSlotsFromWindow(windowStart, windowEnd, duration);
          });
          return {
            id:   item.user_id ?? item.inspector_id ?? item.id,
            name: item.user_name ?? item.name ?? `${item.first_name || ""} ${item.last_name || ""}`.trim(),
            slots,
          };
        });
        setInspectorSchedule(normalised);
        handleFormChange("availableInspectors", normalised);
      })
      .catch((err) => {
        console.error("Failed to fetch inspector schedule:", err);
        setInspectorSchedule([]);
        handleFormChange("availableInspectors", []);
      })
      .finally(() => setIsLoadingSchedule(false));
  }, [formData.appointmentDate, totalServiceDuration]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build available time slots based on selected inspector
  const availableTimeSlots = useMemo(() => {
    if (!formData.appointmentDate || inspectorSchedule.length === 0) return [];

    const windowStart = parseTimeToMinutes(schedulerSettings?.start_time) ?? 0;
    const windowEnd   = parseTimeToMinutes(schedulerSettings?.end_time)   ?? 24 * 60;

    const withinWindow = (slotStart, slotEnd) => {
      const s = parseTimeToMinutes(slotStart);
      const e = parseTimeToMinutes(slotEnd);
      if (s === null || e === null) return true; // can't determine, allow
      return s >= windowStart && e <= windowEnd;
    };

    const selectedId = formData.preferredInspector;

    if (!selectedId || selectedId === "any") {
      // Aggregate all slots across all inspectors, group by time
      const slotMap = new Map();
      inspectorSchedule.forEach((inspector) => {
        inspector.slots.forEach((slot) => {
          const label = `${format24hTo12h(slot.start)} - ${format24hTo12h(slot.end)}`;
          if (!slotMap.has(label)) slotMap.set(label, { start: slot.start, end: slot.end, inspectorNames: [] });
          slotMap.get(label).inspectorNames.push(inspector.name);
        });
      });
      return Array.from(slotMap.entries())
        .filter(([, v]) => withinWindow(v.start, v.end))
        .sort((a, b) => parseTimeToMinutes(a[1].start) - parseTimeToMinutes(b[1].start))
        .map(([label, v]) => ({ time: label, inspectorNames: v.inspectorNames }));
    }

    const inspector = inspectorSchedule.find((i) => String(i.id) === String(selectedId));
    if (!inspector) return [];
    return inspector.slots
      .filter((s) => withinWindow(s.start, s.end))
      .sort((a, b) => parseTimeToMinutes(a.start) - parseTimeToMinutes(b.start))
      .map((s) => ({
        time: `${format24hTo12h(s.start)} - ${format24hTo12h(s.end)}`,
        inspectorNames: [inspector.name],
      }));
  }, [formData.appointmentDate, formData.preferredInspector, inspectorSchedule, schedulerSettings]);

  const handlePrevMonth = () => setCurrentMonth((prev) => addMonths(prev, -1));
  const handleNextMonth = () => setCurrentMonth((prev) => addMonths(prev, 1));

  const handleDateSelect = (date) => {
    handleFormChange("appointmentDate", date);
    handleFormChange("appointmentTime", "");
  };

  const handleTimeSlotSelect = (time) => handleFormChange("appointmentTime", time);

  const handleInspectorSelect = (inspectorId) => {
    handleFormChange("preferredInspector", inspectorId);
    handleFormChange("appointmentTime", "");
  };

  const isDateDisabled = (date) => {
    const config = getConfig();
    if (isBefore(date, today)) return true;
    if (isSameDay(date, today)) {
      const apiAllowSameDay = schedulerSettings?.allow_sameday_scheduling;
      if (apiAllowSameDay !== undefined && apiAllowSameDay !== null) return apiAllowSameDay === 0;
      return config.allowSameDayScheduling === false;
    }
    const tomorrow = addDays(today, 1);
    if (isSameDay(date, tomorrow)) {
      const apiNextDayCutoff = schedulerSettings?.nextday_cutoff;
      const cutoffEnabled = apiNextDayCutoff !== undefined && apiNextDayCutoff !== null
        ? apiNextDayCutoff === 1
        : config.blockNextDayAfterCutoff !== false;
      if (cutoffEnabled) return true;    }
    return false;
  };

  const isPrevMonthDisabled = isBefore(startOfMonth(addMonths(currentMonth, -1)), startOfMonth(today));

  return (
    <div className="p-6 px-[25px] my-0 py-0 mb-[10px]">
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Select Date & Time</h2>
      <p className="text-sm text-muted-foreground mb-6">
        Choose a date, then select an inspector to see available time slots
      </p>
      {(validationErrors.appointmentDate || validationErrors.appointmentTime) && (
        <div className="mb-4 p-2 bg-amber-50 rounded-md text-sm" style={{ color: "#ec0808" }}>
          Please select a date, inspector, and time
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calendar */}
        <div>
          <Label className="text-sm font-medium mb-3 block">1. Choose a Date</Label>
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium">{format(currentMonth, "MMMM yyyy")}</h3>
              <div className="flex space-x-2">
                <Button variant="outline" size="icon" onClick={handlePrevMonth} disabled={isPrevMonthDisabled}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={handleNextMonth}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-7 border-b border-gray-200">
              {dayLabels.map((label) => (
                <div className="text-center py-2 text-sm text-gray-500 font-medium" key={label}>{label}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 p-2 gap-1">
              {Array.from({ length: startDayOfWeek }).map((_, i) => (
                <div className="aspect-square" key={`empty-${i}`} />
              ))}
              {daysInMonth.map((date) => {
                const disabled = isDateDisabled(date);
                const isSelected = formData.appointmentDate && isSameDay(date, formData.appointmentDate);
                const isTodayDate = isSameDay(date, today);
                return (
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => handleDateSelect(date)}
                    className={cn(
                      "aspect-square flex items-center justify-center rounded-lg text-sm transition-colors",
                      disabled && "text-gray-300 cursor-not-allowed",
                      !disabled && !isSelected && "hover:bg-gray-100",
                      isSelected && "bg-green-600 text-white font-medium",
                      isTodayDate && disabled && "border border-gray-300 border-solid text-gray-300 cursor-not-allowed",
                      isTodayDate && !isSelected && !disabled && "border border-green-600 border-solid text-green-600 font-medium"
                    )}
                    key={date.toISOString()}
                  >
                    {format(date, "d")}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Inspector + Time Slots */}
        <div className="space-y-6">
          {formData.appointmentDate ? (
            <>
              {/* Inspector selection */}
              <div>
                <Label className="text-sm font-medium mb-3 block">
                  2. Select Inspector
                  <span className="font-normal text-muted-foreground ml-2">
                    for {format(formData.appointmentDate, "EEEE, MMM d")}
                  </span>
                </Label>

                {isLoadingSchedule ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading available inspectors...
                  </div>
                ) : (
                  <RadioGroup
                    value={formData.preferredInspector || "any"}
                    onValueChange={handleInspectorSelect}
                    className="space-y-2"
                  >
                    {/* Any Available */}
                    <label
                      htmlFor="inspector-any"
                      className={cn(
                        "flex items-center justify-between p-3 rounded-lg border border-solid transition-all cursor-pointer",
                        formData.preferredInspector === "any" || !formData.preferredInspector
                          ? "border-green-600 bg-green-50 ring-1 ring-green-600"
                          : "border-gray-200 hover:border-green-400"
                      )}
                    >
                      <div className="flex items-center space-x-3">
                        <RadioGroupItem value="any" id="inspector-any" className="border-green-600 border-solid text-green-600" />
                        <span className="cursor-pointer font-medium text-sm">
                          Any Available Inspector
                        </span>
                      </div>
                    </label>

                    {/* Individual inspectors */}
                    {schedulerSettings?.display_inspector_names !== 0 &&
                      inspectorSchedule.map((inspector) => {
                        const isAvailable = inspector.slots.length > 0;
                        return (
                          <label
                            key={inspector.id}
                            htmlFor={isAvailable ? `inspector-${inspector.id}` : undefined}
                            className={cn(
                              "flex items-center justify-between p-3 rounded-lg border transition-all border-solid",
                              !isAvailable && "opacity-50 cursor-not-allowed",
                              isAvailable && "cursor-pointer",
                              formData.preferredInspector === String(inspector.id)
                                ? "border-green-600 bg-green-50 ring-1 ring-green-600"
                                : isAvailable
                                  ? "border-gray-200 hover:border-green-400"
                                  : "border-gray-200"
                            )}
                          >
                            <div className="flex items-center space-x-3">
                              <RadioGroupItem
                                value={String(inspector.id)}
                                id={`inspector-${inspector.id}`}
                                disabled={!isAvailable}
                                className="border-green-600 border-solid text-green-600"
                              />
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4 text-muted-foreground" />
                                <span className={cn("text-sm", !isAvailable && "text-muted-foreground")}>
                                  {inspector.name}
                                </span>
                              </div>
                            </div>
                            {!isAvailable && <span className="text-xs text-gray-500">Unavailable</span>}
                          </label>
                        );
                      })}
                  </RadioGroup>
                )}
              </div>

              {/* Time slots */}
              {formData.preferredInspector && (
                <div>
                  <Label className="text-sm font-medium mb-3 block">3. Choose a Time</Label>
                  {isLoadingSchedule ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading time slots...
                    </div>
                  ) : availableTimeSlots.length > 0 ? (
                    <div className="space-y-2">
                      {availableTimeSlots.map((slot) => (
                        <button
                          type="button"
                          key={slot.time}
                          onClick={() => handleTimeSlotSelect(slot.time)}
                          className={cn(
                            "w-full flex items-center justify-between p-4 rounded-lg border border-solid text-left transition-all",
                            formData.appointmentTime === slot.time
                              ? "border-green-600 bg-green-50 ring-2 ring-green-600"
                              : "border-gray-200 hover:border-green-400 hover:bg-gray-50"
                          )}
                        >
                          <div className="flex items-center gap-3">
                            <Clock className="h-5 w-5 text-muted-foreground" />
                            <div>
                              <span className="font-medium">{slot.time}</span>
                              {schedulerSettings?.display_inspector_names !== 0 && slot.inspectorNames?.length > 0 && (
                                <div className="flex items-center gap-1 mt-0.5">
                                  <User className="h-3 w-3 text-muted-foreground" />
                                  <span className="text-xs text-muted-foreground">
                                    {slot.inspectorNames.join(", ")}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {totalServiceDuration > 0 ? `${totalServiceDuration} Min Inspection` : ""}
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
                      <p className="text-amber-800">
                        No available time slots. Please select another inspector or date.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
              <User className="h-8 w-8 text-gray-400 mx-auto mb-3" />
              <p className="text-muted-foreground text-base">Select a date to see available inspectors</p>
            </div>
          )}
        </div>
      </div>

      {/* Selected appointment summary */}
      {formData.appointmentDate && formData.appointmentTime && (
        <div className="bg-green-50 border border-green-100 rounded-md p-4 mt-6">
          <p className="font-medium">Your selected appointment:</p>
          <p className="text-green-700">
            {format(formData.appointmentDate, "EEEE, MMMM d, yyyy")} at {formData.appointmentTime}
          </p>
          {formData.preferredInspector && formData.preferredInspector !== "any" && (
            <p className="text-sm text-gray-600 mt-1">
              Inspector: {inspectorSchedule.find((i) => String(i.id) === String(formData.preferredInspector))?.name}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default DateTimeForm;